import { useContext, useMemo, useState } from "react";
import { useTable } from "react-table";
import {
  defaultDateDisplay,
  formatCurrency,
  formatDate,
  formatNumberWithCommas,
  getPlatform,
  truncateString,
} from "../../../helpers";
import useSalesModal from "../../../hooks/useSalesModal";
import { useNavigate } from "react-router-dom";
import { platformHandler } from "../../../hooks/context";
import secondaryComponents from "..";

export const useStockHistoryTable = ({ stockHistory }) => {
  const navigate = useNavigate();
  const { platform } = useContext(platformHandler);
  const [previewImage, setPreviewImage] = useState(null);
  const {
    isTnxRecipt,
    isSendRecipt,
    isTerminal,
    openSalesTransactionWidget,
    selectedOrder,
    orderDetails,
    setSelectedOrder,
    setOrderDetails,
    gotoTransactionDetails,
    setOpenSalesTransactionWidget,
    closeWidget,
    openSendReceipt,
    gotoReceiptTerminals,
  } = useSalesModal();

  const formatStockType = (type) => {
    if (!type) return;
    let stockType = "";

    if (type === "NEW_STOCK") stockType = "New Stock";
    if (type === "SOLD_STOCK") stockType = "Sold Stock";
    if (type === "RVSL_SOLD_STOCK") stockType = "Reversed";
    if (type === "DELETED_STOCK") stockType = "Deleted";
    if (type === "STOCK_IN_XFER") stockType = "Transfer In";
    if (type === "STOCK_OUT_XFER") stockType = "Transfer Out";
    if (type === "OVERSTOCKED") stockType = "Overstocked";
    if (type === "EXPIRED") stockType = "Expired";
    if (type === "RETURN") stockType = "Returned";
    if (type === "STOCK_IN_RECONCILIATION")
      stockType = "Stock In Reconciliation";
    if (type === "STOCK_OUT_RECONCILIATION")
      stockType = "Stock Out Reconciliation";
    if (type === "REMOVED_STOCK") stockType = "Removed Stock";
    if (type === "CONSUMED") stockType = "Consumed";
    if (type === "DAMAGED") stockType = "Damaged";
    if (type === "WRONG_ENTRY") stockType = "Wrong Entry";
    if (type === "OPERATIONAL_USE") stockType = "Operational Use";
    if (type === "OTHER") stockType = "Other";

    return stockType;
  };

  const formatStockTypeColorCode = (type) => {
    let color = "";
    const stockType = formatStockType(type);

    if (
      stockType === "New Stock" ||
      stockType === "Transfer In" ||
      stockType === "Stock In Reconciliation" ||
      stockType === "Operational Use"
    )
      color = "#19AE57";
    if (
      stockType === "Sold Stock" ||
      stockType === "Transfer Out" ||
      stockType === "Stock Out Reconciliation" ||
      stockType === "Consumed" ||
      stockType === "Damaged" ||
      stockType === "Wrong Entry" ||
      stockType === "Removed Stock" ||
      stockType === "Other"
    )
      color = "#E02020";
    if (
      stockType === "Reversed" ||
      stockType === "Overstocked" ||
      stockType === "Returned"
    )
      color = "#F7AD3C";
    if (stockType === "Deleted" || stockType === "Expired") color = "#E02020";
    return color;
  };

  const columns = useMemo(
    () => [
      {
        Header: "Date/Time",
        accessor: "created_at",
        Cell: ({ cell: { value } }) => defaultDateDisplay(value),
      },
      {
        Header: "Product Name",
        accessor: (originalRow) => {
          if (originalRow?.product === null) {
            return originalRow?.product_name;
          }

          return originalRow?.product;
        },
        Cell: ({ cell: { value } }) => {
          if (value?.name !== undefined) {
            return truncateString(value?.name, 200);
          }
          if (value === null) return "N/A";

          return truncateString(value, 200);
        },
      },
      {
        Header: "Order ID",
        accessor: "order",
        Cell: ({ cell: { value } }) => {
          if (value) {
            return (
              <span
                style={{
                  color: "#7647EE",
                  height: "28px",
                  cursor: "pointer",
                }}
                onClick={() =>
                  navigate(
                    getPlatform(platform) +
                      `/inventory/sales-transactions?orderId=${value?.id}`,
                  )
                }
              >
                {value?.id}
              </span>
            );
          } else return "-";
        },
      },
      {
        Header: "Variant",
        accessor: (originalRow) => {
          if (originalRow?.variant === null) {
            return originalRow?.variant_name;
          }

          return originalRow?.variant;
        },
        Cell: ({ cell: { value } }) => {
          if (typeof value === "string") return value;
          if (typeof value === "object") {
            if (typeof value?.option !== "undefined") {
              return `${value?.option}-${value?.value}`;
            }
          }

          return "N/A";
        },
      },
      {
        Header: "Stock type",
        accessor: "formatted_type",
        Cell: ({ cell: { value }, row }) => {
          return value === "REMOVED_STOCK" ? (
            <secondaryComponents.ToolTipV2
              tipText={
                row?.original?.type_description ||
                formatStockType(row?.original?.type)
              }
            >
              <span style={{ color: formatStockTypeColorCode(value) }}>
                {formatStockType(value)}
              </span>
            </secondaryComponents.ToolTipV2>
          ) : (
            <span style={{ color: formatStockTypeColorCode(value) }}>
              {formatStockType(value)}
            </span>
          );
        },
      },
      {
        Header: "Evidence",
        accessor: "images",
        Cell: ({ cell: { value } }) => {
          if (Array.isArray(value) && value.length > 0) {
            return (
              <div
                style={{
                  display: "flex",
                  gap: "6px",
                  alignItems: "center",
                }}
              >
                {value.slice(0, 2).map((imgUrl, idx) => (
                  <img
                    key={idx}
                    src={imgUrl}
                    alt="evidence"
                    style={{
                      width: "32px",
                      height: "32px",
                      objectFit: "cover",
                      borderRadius: "4px",
                      cursor: "pointer",
                      border: "1px solid #E2E8F0",
                    }}
                    onClick={() => setPreviewImage(imgUrl)}
                  />
                ))}
                {value.length > 2 && (
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#7647EE",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                    onClick={() => setPreviewImage(value[0])}
                  >
                    +{value.length - 2}
                  </span>
                )}
              </div>
            );
          }
          return "-";
        },
      },
      {
        Header: "Expiry Date",
        accessor: "removal_expiry_date",
        Cell: ({ cell: { value } }) =>
          value ? defaultDateDisplay(value) : "-",
      },
      {
        Header: "Quantity",
        accessor: "quantity",
        Cell: ({ cell: { value } }) =>
          value ? formatNumberWithCommas(value) : "0",
      },
      {
        Header: "Quantity Before",
        accessor: "quantity_before",
        Cell: ({ cell: { value } }) =>
          value ? formatNumberWithCommas(value) : "0",
      },
      {
        Header: "Quantity After",
        accessor: "quantity_after",
        Cell: ({ cell: { value } }) =>
          value ? formatNumberWithCommas(value) : "0",
      },
      {
        Header: "Product VAT",
        accessor: "vat_amount",
        Cell: ({ cell: { row } }) => {
          if (row?.original?.product?.include_vat_in_price === true) return "0";
          const productVat = row?.original?.product?.vat || 0;
          const storeVat = row?.original?.store?.vat || 0;
          const vat = productVat ? productVat : storeVat;
          const sellingPrice = row?.original?.selling_price || 0;
          const currencyCode = row?.original?.store?.currency_code;
          const vatAmount =
            row?.original?.type === "CONSUMED" ? 0 : (vat / 100) * sellingPrice;
          return formatCurrency({
            value: vatAmount,
            currencyCode,
          });
        },
      },
      {
        Header: "Cost Price",
        accessor: "cost_price",
        Cell: ({ cell }) =>
          formatCurrency({
            value: cell.value,
            currencyCode: cell?.row?.original?.store?.currency_code,
          }),
      },
      {
        Header: "Selling Price",
        accessor: "selling_price",
        Cell: ({ cell }) =>
          formatCurrency({
            value: cell.value,
            currencyCode: cell?.row?.original?.store?.currency_code,
          }),
      },
      {
        Header: "Initiator",
        accessor: "initiator",
        Cell: ({ cell: { value } }) =>
          value !== null ? value?.first_name : "N/A",
      },
      {
        Header: "Store ",
        accessor: "store",
        Cell: ({ cell: { value } }) => <span>{value?.name || "N/A"}</span>,
      },
      {
        Header: "Reference Store",
        accessor: "reference_store",
        Cell: ({ cell: { value } }) => <span>{value?.name || "N/A"}</span>,
      },
      {
        Header: "Tracking Number",
        accessor: "inventory_items",
        Cell: ({ cell: { value } }) => {
          const values = value?.map((item) => item);
          return (
            <secondaryComponents.ToolTipV2 tipText={values?.join(", ")}>
              {values?.length > 0
                ? truncateString(values?.join(", "), 50)
                : "N/A"}
            </secondaryComponents.ToolTipV2>
          );
        },
      },
      {
        Header: "Supplier",
        accessor: "supplier",
        Cell: ({ cell: { value } }) => (value !== null ? value?.name : "N/A"),
      },
      {
        Header: "Restock Date",
        accessor: "restock_date",
        Cell: ({ cell: { value } }) => {
          return value ? formatDate(value) : "N/A";
        },
      },
      {
        Header: "Batch",
        accessor: "batch_name",
        Cell: ({ cell: { value } }) =>
          value ? truncateString(value, 20) : "N/A",
      },
    ],
    [],
  );
  const instance = useTable({ columns, data: stockHistory });

  return {
    instance,
    previewImage,
    setPreviewImage,
    isTnxRecipt,
    isSendRecipt,
    isTerminal,
    openSalesTransactionWidget,
    selectedOrder,
    orderDetails,
    setSelectedOrder,
    setOrderDetails,
    gotoTransactionDetails,
    setOpenSalesTransactionWidget,
    closeWidget,
    openSendReceipt,
    gotoReceiptTerminals,
  };
};
